// src/pages/FirmHearingsPage.jsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "./components/DashboardShell";

function formatDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getHearingDocketText(h) {
  return (
    h?.last_updated_from_entry?.docket_text ||
    h?.created_from_entry?.docket_text ||
    h?.caption ||
    "Hearing"
  );
}

function getHearingDocuments(h) {
  return h?.last_updated_from_entry?.documents || h?.created_from_entry?.documents || [];
}

function getDocUrl(doc) {
  return doc?.file_url || doc?.file || doc?.url || "";
}

function getDocLabel(doc, i) {
  return doc?.description || `Document ${i + 1}`;
}

function truncate(s, n = 2180) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}

function isoToLocalDateKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const API_URL = process.env.REACT_APP_API_URL;

function normalizeText(s) {
  return (s || "").toString().toLowerCase();
}

function parseTerms(input) {
  return (input || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function StatusPill({ status }) {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-xs border";
  const map = {
    scheduled: "bg-emerald-50 text-emerald-800 border-emerald-200",
    continued: "bg-amber-50 text-amber-800 border-amber-200",
    reset: "bg-blue-50 text-blue-800 border-blue-200",
    held: "bg-gray-50 text-gray-800 border-gray-200",
    canceled: "bg-rose-50 text-rose-800 border-rose-200",
    unknown: "bg-gray-50 text-gray-800 border-gray-200",
  };
  return <span className={`${base} ${map[status] || map.unknown}`}>{status}</span>;
}

function CaseCell({ h, selectedCaseId, onToggleCaseFilter }) {
  const title = h?.case_title || "";
  const num = h?.case_number || "";
  const label = [title, num].filter(Boolean).join(" — ");

  const isSelected = selectedCaseId === h.case_id;

  return (
    <div className="min-w-[240px]">
      <Link
        to={`/case/${h.case_id}/hearings`}
        className="text-sm font-medium text-blue-700 hover:underline"
        title={label}
      >
        {title || "(Untitled case)"}
      </Link>

      <div className="mt-0.5 flex items-center gap-2">
        <div className="text-xs text-gray-600">{num}</div>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleCaseFilter?.(h.case_id, h.id);
          }}
          className={`rounded border px-2 py-0.5 text-[11px] font-medium shadow-sm hover:bg-gray-50 ${
            isSelected
              ? "border-blue-300 bg-blue-50 text-blue-800"
              : "border-gray-300 bg-white text-gray-800"
          }`}
          title={isSelected ? "Show hearings from all cases" : "Show only hearings from this case"}
        >
          {isSelected ? "Show all cases" : "Show only this case"}
        </button>
      </div>
    </div>
  );
}

export default function FirmHearingsPage() {
  const [hearings, setHearings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [filterMode, setFilterMode] = useState("exclude"); // 'exclude' | 'include'
  const [filterText, setFilterText] = useState("Chap 13 Conf");
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // YYYY-MM-DD

  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const [totalCount, setTotalCount] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);

  const rowRefs = useRef({}); // { [hearingId]: { mobile?: HTMLElement, desktop?: HTMLElement } }
  const [scrollToHearingId, setScrollToHearingId] = useState(null);
  function toggleSingleDateFilter(iso, hearingId) {
    const dateKey = isoToLocalDateKey(iso);
    if (!dateKey) return;

    if (hearingId) setScrollToHearingId(hearingId);

    // If already filtering to this exact single day, clear.
    if (startDate === dateKey && endDate === dateKey) {
      setStartDate("");
      setEndDate("");
      return;
    }

    setStartDate(dateKey);
    setEndDate(dateKey);
  }
  function toggleCaseFilter(caseId, hearingId) {
    if (hearingId) setScrollToHearingId(hearingId);
    setSelectedCaseId((prev) => (prev === caseId ? null : caseId));
  }

  useEffect(() => {
    const controller = new AbortController();

    async function loadAllPages() {
      try {
        setLoading(true);
        setErr("");
        setHearings([]);
        setTotalCount(null);
        setLoadedCount(0);

        const token =
          localStorage.getItem("access") ||
          localStorage.getItem("accessToken") ||
          localStorage.getItem("token") ||
          "";

        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        // Request a large page size to minimize round-trips, but still paginate.
        let nextUrl = `${API_URL}/firm/hearings/filed-ch13/?page_size=50`;

        while (nextUrl) {
          const res = await fetch(nextUrl, {
            method: "GET",
            headers,
            credentials: "include",
            signal: controller.signal,
          });

          if (!res.ok) {
            let detail = "";
            try {
              const data = await res.json();
              detail = data?.detail || data?.message || "";
            } catch (e) {}
            throw new Error(detail || `Failed to load hearings (HTTP ${res.status})`);
          }

          const payload = await res.json();

          // DRF pagination: { count, next, previous, results }
          const results = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);

          if (typeof payload?.count === "number") {
            setTotalCount(payload.count);
          }

          if (results.length) {
            setHearings((prev) => prev.concat(results));
            setLoadedCount((prev) => prev + results.length);
          }

          nextUrl = payload?.next || null;
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        setErr(e?.message || "Failed to load hearings");
      } finally {
        setLoading(false);
      }
    }

    loadAllPages();

    return () => controller.abort();
  }, []);

  const terms = parseTerms(filterText).map(normalizeText);

  const filteredHearings = hearings.filter((h) => {
    const dateKey = isoToLocalDateKey(h?.starts_at);

    if (selectedCaseId && h?.case_id !== selectedCaseId) return false;

    if (startDate && dateKey && dateKey < startDate) return false;
    if (endDate && dateKey && dateKey > endDate) return false;

    if (terms.length === 0) return true;
    const hay = normalizeText(getHearingDocketText(h));
    const matchesAny = terms.some((t) => hay.includes(t));
    return filterMode === "include" ? matchesAny : !matchesAny;
  });

  useLayoutEffect(() => {
    if (!scrollToHearingId) return;

    // Let React commit the DOM for the new filtered list, then scroll.
    const nodes = rowRefs.current?.[scrollToHearingId];
    const el = nodes?.mobile?.offsetParent ? nodes.mobile : (nodes?.desktop?.offsetParent ? nodes.desktop : (nodes?.mobile || nodes?.desktop));
    if (!el) return;

    // Use rAF to ensure layout is stable.
    const raf = requestAnimationFrame(() => {
      try {
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      } finally {
        // Clear so we don't keep re-scrolling.
        setScrollToHearingId(null);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [scrollToHearingId, selectedCaseId, startDate, endDate, filterMode, filterText]);

  if (err) {
    return (
      <DashboardShell>
        <div className="text-red-600">{err}</div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="space-y-4 text-gray-900">
        {loading ? (() => {
          const pct = totalCount ? Math.min(100, Math.round((loadedCount / totalCount) * 100)) : null;
          return (
            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-gray-700">
                  Loading hearings{pct !== null ? `… ${pct}%` : "…"}
                </div>
                <div className="text-xs text-gray-500">
                  {totalCount ? `Loaded ${loadedCount} of ${totalCount}` : `Loaded ${loadedCount}`}
                </div>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-200">
                <div
                  className="h-2 bg-blue-600"
                  style={{ width: `${pct !== null ? pct : 20}%` }}
                />
              </div>
            </div>
          );
        })() : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Firm Hearings</h1>
            <p className="text-sm text-gray-600">
              Hearings from all filed Chapter 13 cases.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700">Filter mode</label>
              <select
                className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
              >
                <option value="exclude">Exclude matching</option>
                <option value="include">Include only matching</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700">Filter terms (comma-separated)</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-[360px]"
                placeholder='e.g. "relief from stay, objection, confirmation"'
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700">Start date</label>
              <input
                type="date"
                className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700">End date</label>
              <input
                type="date"
                className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50"
              onClick={() => {
                setFilterText("");
                setStartDate("");
                setEndDate("");
              }}
              disabled={!filterText && !startDate && !endDate}
              title="Clear filter"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-200">
            {filteredHearings.map((h) => (
              <div
                key={h.id}
                ref={(node) => {
                  if (node) {
                    rowRefs.current[h.id] = { ...(rowRefs.current[h.id] || {}), mobile: node };
                  }
                }}
                className="p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500">Case</div>
                    <div className="mt-0.5">
                      <CaseCell
                        h={h}
                        selectedCaseId={selectedCaseId}
                        onToggleCaseFilter={toggleCaseFilter}
                      />
                    </div>
                  </div>

                  <div className="shrink-0">
                    <StatusPill status={h.status} />
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-xs text-gray-500">Date/Time</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                    <div>{formatDT(h.starts_at)}</div>
                    {(() => {
                      const dk = isoToLocalDateKey(h?.starts_at);
                      if (!dk) return null;
                      const isActive = startDate === dk && endDate === dk;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSingleDateFilter(h?.starts_at, h?.id);
                          }}
                          className={`rounded border px-2 py-0.5 text-[11px] font-medium shadow-sm hover:bg-gray-50 ${
                            isActive
                              ? "border-blue-300 bg-blue-50 text-blue-800"
                              : "border-gray-300 bg-white text-gray-800"
                          }`}
                          title={isActive ? "Clear date filter" : `Show only hearings on ${dk}`}
                        >
                          {isActive ? "Show all dates" : "Show only this date"}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-xs text-gray-500">Docket text</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {truncate(getHearingDocketText(h), 220)}
                  </div>

                  {getHearingDocuments(h)?.length ? (
                    <div className="mt-2 flex flex-col gap-1">
                      {getHearingDocuments(h)
                        .map((doc, i) => {
                          const href = getDocUrl(doc);
                          if (!href) return null;
                          return (
                            <a
                              key={doc?.id || i}
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-700 hover:underline"
                            >
                              {getDocLabel(doc, i)}
                            </a>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  ) : null}

                  {h.supersedes_id ? (
                    <div className="mt-1 text-xs text-gray-500">(rescheduled)</div>
                  ) : null}
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2">
                  <div>
                    <div className="text-xs text-gray-500">Type</div>
                    <div className="mt-0.5 text-sm text-gray-700">{h.hearing_type}</div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Location</div>
                    <div className="mt-0.5 text-sm text-gray-700">
                      {[h.location_name, h.location_city, h.location_state]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {!loading && filteredHearings.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">
                No firm hearings found (filed Chapter 13).
              </div>
            ) : null}
          </div>

          {/* Desktop table */}
          <table className="hidden sm:table min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Case</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date/Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Docket text</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filteredHearings.map((h) => (
                <tr
                  key={h.id}
                  ref={(node) => {
                    if (node) {
                      rowRefs.current[h.id] = { ...(rowRefs.current[h.id] || {}), desktop: node };
                    }
                  }}
                  className="hover:bg-gray-50 align-top"
                >
                  <td className="px-4 py-3">
                    <CaseCell h={h} selectedCaseId={selectedCaseId} onToggleCaseFilter={toggleCaseFilter} />
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div>{formatDT(h.starts_at)}</div>
                      {(() => {
                        const dk = isoToLocalDateKey(h?.starts_at);
                        if (!dk) return null;
                        const isActive = startDate === dk && endDate === dk;
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleSingleDateFilter(h?.starts_at, h?.id);
                            }}
                            className={`rounded border px-2 py-0.5 text-[11px] font-medium shadow-sm hover:bg-gray-50 ${
                              isActive
                                ? "border-blue-300 bg-blue-50 text-blue-800"
                                : "border-gray-300 bg-white text-gray-800"
                            }`}
                            title={isActive ? "Clear date filter" : `Show only hearings on ${dk}`}
                          >
                            {isActive ? "All dates" : "Only date"}
                          </button>
                        );
                      })()}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div className="text-gray-900">{truncate(getHearingDocketText(h))}</div>

                    {getHearingDocuments(h)?.length ? (
                      <div className="mt-2 flex flex-col gap-1">
                        {getHearingDocuments(h)
                          .map((doc, i) => {
                            const href = getDocUrl(doc);
                            if (!href) return null;
                            return (
                              <a
                                key={doc?.id || i}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-700 hover:underline"
                              >
                                {getDocLabel(doc, i)}
                              </a>
                            );
                          })
                          .filter(Boolean)}
                      </div>
                    ) : null}

                    {h.supersedes_id ? (
                      <div className="mt-1 text-xs text-gray-500">(rescheduled)</div>
                    ) : null}
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-700">{h.hearing_type}</td>

                  <td className="px-4 py-3 text-sm text-gray-700">
                    {[h.location_name, h.location_city, h.location_state].filter(Boolean).join(", ")}
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-700">
                    <StatusPill status={h.status} />
                  </td>
                </tr>
              ))}

              {!loading && filteredHearings.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={6}>
                    No firm hearings found (filed Chapter 13).
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardShell>
  );
}